import { useNavigate } from 'react-router-dom';

import { Stack, Tag } from '@ekhodealer/ekho-common/rev';

import type { ReactNode } from 'react';

import {
	Blocks24,
	DocumentText24,
	Globe24,
	Inventory24,
	Leads24,
	ReceiptText24,
	Sparkles24,
	WalletCard24,
} from '@common/components/icons';
import { NavigationBox } from '@common/components/NavigationBox/NavigationBox';
import { NavigationGrid } from '@common/components/NavigationGrid/NavigationGrid';
import { PageLayout } from '@common/components/PageLayout';
import { useCurrentServicesProviderId } from '@common/hooks';
import { useProductGating } from '@common/hooks/useProductGating';
import { paths } from '@config/paths';
import { SettingsSection } from '@config/paths/settingsSections';

export default function SettingsPage(): React.ReactNode {
	const navigate = useNavigate();
	const spId = useCurrentServicesProviderId();
	const { isModuleLocked, getSettingsSectionGate } = useProductGating();

	const getBadgeForSection = (section: SettingsSection): ReactNode => {
		const { locked } = getSettingsSectionGate(section);
		if (!locked) return null;
		return (
			<Tag size='small' accent={1} indicator={false}>
				New
			</Tag>
		);
	};

	const showInventory = !isModuleLocked('transactionLayer') || !isModuleLocked('aiBdc');

	return (
		<PageLayout title='Settings'>
			<Stack itemsSpacing='32'>
				<NavigationGrid title='Account settings'>
					<NavigationBox
						icon={<DocumentText24 />}
						title='Business details'
						description='Manage dealership identity, address, and contact info.'
						onPress={() =>
							navigate(
								paths.settingsSection.getHref(
									spId,
									SettingsSection.AccountBusinessDetails,
								),
							)
						}
					/>
					<NavigationBox
						icon={<WalletCard24 />}
						title='Bank account'
						description='Set up payment deposit accounts.'
						onPress={() =>
							navigate(
								paths.settingsSection.getHref(
									spId,
									SettingsSection.AccountBankAccount,
								),
							)
						}
					/>
					<NavigationBox
						icon={<Leads24 />}
						title='Team'
						description='Manage team members and permissions.'
						onPress={() =>
							navigate(
								paths.settingsSection.getHref(spId, SettingsSection.AccountTeam),
							)
						}
					/>
				</NavigationGrid>
				<NavigationGrid title='Product settings'>
					<NavigationBox
						icon={<ReceiptText24 />}
						title='Sales'
						description='Configure checkout, financing, and transaction settings.'
						badge={getBadgeForSection(SettingsSection.ProductsSales)}
						onPress={() =>
							navigate(
								paths.settingsSection.getHref(spId, SettingsSection.ProductsSales),
							)
						}
					/>
					<NavigationBox
						icon={<Globe24 />}
						title='Website'
						description='Configure your dealer website pages and content.'
						badge={getBadgeForSection(SettingsSection.ProductsWebsite)}
						onPress={() =>
							navigate(
								paths.settingsSection.getHref(
									spId,
									SettingsSection.ProductsWebsite,
								),
							)
						}
					/>
					{!isModuleLocked('aiBdc') && (
						<NavigationBox
							icon={<Sparkles24 />}
							title='AI BDC'
							description='Configure your AI sales assistant — persona, scheduling, and behavior.'
							badge={getBadgeForSection(SettingsSection.ProductsAiBdc)}
							onPress={() =>
								navigate(
									paths.settingsSection.getHref(spId, SettingsSection.ProductsAiBdc),
								)
							}
						/>
					)}
				</NavigationGrid>
				<NavigationGrid title='Resource settings'>
					<NavigationBox
						icon={<Blocks24 />}
						title='Integrations'
						description='Manage CRM forwarding and DMS connections.'
						onPress={() =>
							navigate(
								paths.settingsSection.getHref(
									spId,
									SettingsSection.SharedIntegrations,
								),
							)
						}
					/>
					{showInventory && (
						<NavigationBox
							icon={<Inventory24 />}
							title='Inventory'
							description='Configure inventory sources, website scraping, and field precedence.'
							onPress={() =>
								navigate(
									paths.settingsSection.getHref(
										spId,
										SettingsSection.SharedInventory,
									),
								)
							}
						/>
					)}
				</NavigationGrid>
			</Stack>
		</PageLayout>
	);
}
